import React from "react";
import { styledSystem as ss, classNames, theme } from ".";

export type ITooltipProps = ss.ICommonProps &
	ss.ILayoutProps &
	ss.IFontSizeProps &
	ss.ITypographyProps & {
		direction: "n" | "ne" | "e" | "se" | "s" | "sw" | "w" | "nw";
		align?: "left" | "right";
		noDelay?: boolean;
		wrap?: boolean;
		children: React.ReactNode;
		text: string;
		className?: string | any;
	};

function BaseTooltip({ direction, children, noDelay, align, wrap, text, className, ...props }: ITooltipProps) {
	const classes = classNames([`tooltipped-${direction}`, align && `tooltipped-align-${align}-2`, noDelay && "tooltipped-no-delay", wrap && "tooltipped-multiline"], className);
	return (
		<span aria-label={text} {...props} className={classes}>
			{children}
		</span>
	);
}

export const Tooltip = ss.styled(BaseTooltip)`
  position: relative;

  &::before {
    position: absolute;
    z-index: ${(props) => ss.get(props.theme, "zIndices.tooltip")};
    width: 0px;
    height: 0px;
    color: ${(props) => ss.get(props.theme, "colors.black")};
    pointer-events: none;
    content: '';
    border: 6px solid transparent;
    opacity: 0;
  }

  &::after {
	position: absolute;
    z-index: ${(props) => ss.get(props.theme, "zIndices.tooltip")};
    padding: 0.5em 0.75em;
    font: normal normal 11px/1.5 ${(props) => ss.get(props.theme, "fonts.normal")};
    color: ${(props) => ss.get(props.theme, "colors.white")};
    text-align: center;
    text-decoration: none;
    text-shadow: none;
    text-transform: none;
    letter-spacing: normal;
    word-wrap: break-word;
    white-space: pre;
    pointer-events: none;
    content: attr(aria-label);
    background: ${(props) => ss.get(props.theme, "colors.black")};
    border-radius: ${(props) => ss.get(props.theme, "radii.1")};
    opacity: 0;
  }

  // delay animation for tooltip
  @keyframes tooltip-appear {
    from {
      opacity: 0;
    }

    to {
      opacity: 1;
    }
  }

  &:hover,
  &:active,
  &:focus {
    &::before,
    &::after {
      display: inline-block;
      text-decoration: none;
      animation-name: tooltip-appear;
      animation-duration: 0.1s;
      animation-fill-mode: forwards;
      animation-timing-function: ease-in;
      animation-delay: 0.4s;
    }
  }

  &.tooltipped-no-delay:hover,
  &.tooltipped-no-delay:active,
  &.tooltipped-no-delay:focus {
    &::before,
    &::after {
      animation-delay: 0s;
    }
  }

  &.tooltipped-multiline:hover,
  &.tooltipped-multiline:active,
  &.tooltipped-multiline:focus {
    &::after {
      display: table-cell;
    }
  }

  // Tooltipped south
  &.tooltipped-s,
  &.tooltipped-se,
  &.tooltipped-sw {
    &::after {
      top: 100%;
      right: 50%;
      margin-top: 6px;
    }

    &::before {
      top: auto;
      right: 50%;
      bottom: -7px;
      margin-right: -6px;
      border-bottom-color: ${(props) => ss.get(props.theme, "colors.black")};
    }
  }

  &.tooltipped-se {
    &::after {
      right: auto;
      left: 50%;
      margin-left: -${(props) => ss.get(props.theme, "space.3")};
    }
  }

  &.tooltipped-sw::after {
    margin-right: -${(props) => ss.get(props.theme, "space.3")};
  }

  // Tooltips above the object
  &.tooltipped-n,
  &.tooltipped-ne,
  &.tooltipped-nw {
    &::after {
      right: 50%;
      bottom: 100%;
      margin-bottom: 6px;
    }

    &::before {
      top: -7px;
      right: 50%;
      bottom: auto;
      margin-right: -6px;
      border-top-color: ${(props) => ss.get(props.theme, "colors.black")};
    }
  }

  &.tooltipped-ne {
    &::after {
      right: auto;
      left: 50%;
      margin-left: -${(props) => ss.get(props.theme, "space.3")};
    }
  }

  &.tooltipped-nw::after {
    margin-right: -${(props) => ss.get(props.theme, "space.3")};
  }

  // Move the tooltip body to the center of the object.
  &.tooltipped-s::after,
  &.tooltipped-n::after {
    transform: translateX(50%);
  }

  // Tooltipped to the left
  &.tooltipped-w {
    &::after {
      right: 100%;
      bottom: 50%;
      margin-right: 6px;
      transform: translateY(50%);
    }

    &::before {
      top: 50%;
      bottom: 50%;
      left: -7px;
      margin-top: -6px;
      border-left-color: ${(props) => ss.get(props.theme, "colors.black")};
    }
  }

  // tooltipped to the right
  &.tooltipped-e {
    &::after {
      bottom: 50%;
      left: 100%;
      margin-left: 6px;
      transform: translateY(50%);
    }

    &::before {
      top: 50%;
      right: -7px;
      bottom: 50%;
      margin-top: -6px;
      border-right-color: ${(props) => ss.get(props.theme, "colors.black")};
    }
  }

  &.tooltipped-multiline {
    &::after {
      width: max-content;
      max-width: 250px;
      word-wrap: break-word;
      white-space: pre-line;
      border-collapse: separate;
    }

    &.tooltipped-s::after,
    &.tooltipped-n::after {
      right: auto;
      left: 50%;
      transform: translateX(-50%);
    }

    &.tooltipped-w::after,
    &.tooltipped-e::after {
      right: 100%;
    }
  }

  &.tooltipped-align-right-2::after {
    right: 0;
    margin-right: 0;
  }

  &.tooltipped-align-right-2::before {
    right: 15px;
  }

  &.tooltipped-align-left-2::after {
    left: 0;
    margin-left: 0;
  }

  &.tooltipped-align-left-2::before {
    left: 10px;
  }
`;

Tooltip.defaultProps = {
	theme,
};
